/**
 * AppModal — the application's only modal primitive.
 *
 * Why this component exists
 * =========================
 * Every modal in the app MUST go through this wrapper so that a single
 * code path owns the iOS-Safari-safe layout (sticky-footer flex shell,
 * 100svh sizing with pre-Safari-15.4 fallbacks, and home-indicator
 * safe-area padding). Any modal that bypasses AppModal will reproduce
 * the "CTA button slides off-screen on real iPhone" bug class that
 * AppModal is here to make architecturally impossible.
 *
 * Enforcement layers (cross-cutting; see plan)
 *   1. TypeScript — `id` and `footer` are both REQUIRED props.
 *   2. Inline shell — `buildAppModalViewportShellStyles` on Modal content/body
 *                  so sticky footers survive CSS import-order regressions
 *                  (every future AppModal inherits this; no per-modal CSS).
 *   3. Vitest    — `src/test/modalCoverage.test.ts` walks every *.tsx in
 *                  src/ via the TS compiler API and asserts each
 *                  `<AppModal id="literal">` has a Playwright registry
 *                  entry at e2e/sticky-footers.mobile.spec.ts.
 *   4. AST/import — `noRawModalImport.test.ts` + `cssEntryOrder.test.ts`.
 *   5. Cursor    — `.cursor/rules/sticky-modal-footer.mdc` documents the
 *                  contract so AI-generated modal code defaults to AppModal.
 *   6. Playwright— the registry-driven matrix opens every modal on iPhone
 *                  SE and asserts the primary CTA is visible and reachable.
 *
 * Layout pattern (the part hand-rolled modals get wrong)
 * ======================================================
 * Footer is a FLEX SIBLING of the scrollable body, NOT a child of it.
 * `position: sticky` only works relative to the nearest scrolling
 * ancestor — a sibling-of-scroller stays static. The CSS module pins
 * the footer with `flex: 0 0 auto` and pads its bottom by
 * `max(spacing-md, env(safe-area-inset-bottom))` so the home-indicator
 * bar never overlaps the CTA.
 *
 * Mobile-width handling
 * =====================
 * Many existing modals declare `size='xl'` or pixel widths that
 * horizontally clip iPhone SE (375px). AppModal automatically renders
 * with `fullScreen` below 576px (Mantine xs breakpoint, matches the
 * convention already in use across the codebase). Migrations don't
 * have to thread responsive logic through every call site.
 */

import {
  Children,
  isValidElement,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useModalFormEscapeClose } from '../../hooks/useModalFormEscapeClose';
import { Modal, type ModalProps as MantineModalProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { trackModalOpen, trackModalClosed } from '../../services/marketingTracking';
import {
  beginModalAnalyticsSession,
  getModalAnalyticsClosePayload,
} from '../../services/modalAnalyticsSession';
import classes from './AppModal.module.css';
import { buildAppModalViewportShellStyles } from './appModalViewportShellStyles';

/**
 * Stable identifier for an AppModal instance.
 *
 * Each `<AppModal>` in the codebase MUST pass an id that is:
 *   - kebab-case (no spaces, no underscores; matches the existing
 *     Cursor rule conventions for selector-friendly identifiers),
 *   - unique across the application,
 *   - present as a key in the Playwright matrix registry at
 *     `ScheduledSmiles.Web/e2e/sticky-footers.mobile.spec.ts`.
 *
 * Why `string` and not a closed string-literal union? The plan calls for
 * a hand-maintained union, but at the moment AppModal lands no modal
 * has been migrated yet — an empty union (`never`) cannot accept any
 * value and would block migrations. The Vitest AST coverage guard
 * (`src/test/modalCoverage.test.ts`) provides equivalent enforcement at
 * the same job timing (FE test gate), catching every missing or stale
 * registry entry. Once the migration is complete this can be tightened
 * to a closed union by replacing this `string` with the literal union
 * of all registered ids; no AppModal call sites need to change.
 */
export type ModalId = string;

/**
 * `bodyHeight='fill'` — the default. Body grows to fill the small
 * viewport (`100svh` with layered fallback) so the footer pins at the
 * bottom regardless of content length. Use for forms, wizards, lists.
 *
 * `bodyHeight='auto'` — modal shrink-wraps to its content (capped at
 * `100svh`). Use for short confirmation modals where a viewport-tall
 * shell would feel oversized.
 */
export type AppModalBodyHeight = 'fill' | 'auto';

/** Props owned by AppModal — never forwarded to the underlying Mantine Modal. */
type AppModalOwnProps = {
  /**
   * REQUIRED. Stable id used by the Playwright matrix registry and the
   * Vitest AST coverage guard. See {@link ModalId} for the contract.
   */
  id: ModalId;

  /**
   * REQUIRED. Footer slot, typically a `<Group justify="flex-end">` of
   * action buttons. Pass `null` to opt out of a footer entirely (still
   * goes through the wrapper for layout / iOS-safe sizing).
   *
   * The required `null | ReactNode` (no `?`) is intentional: every
   * AppModal author must consciously decide whether their modal has a
   * CTA bar. That decision being explicit is the whole reason this
   * wrapper exists.
   */
  footer: ReactNode | null;

  /**
   * Body sizing strategy. See {@link AppModalBodyHeight}. Defaults to
   * `'fill'` since the most common modal in the app is a form with a
   * sticky CTA.
   */
  bodyHeight?: AppModalBodyHeight;

  /**
   * When true, AppModal does NOT wrap children in its scrollable body
   * region — the caller manages scrolling itself. Use for modals that
   * already render their own ScrollArea (e.g. CardPreviewModal renders
   * a phone-frame canvas) or for modals where overflow is handled by
   * a child component.
   *
   * The footer is still pinned by the flex shell when `noScrollArea`
   * is true; only the body slot's overflow behavior changes.
   */
  noScrollArea?: boolean;

  /**
   * When true, force `fullScreen` rendering at every viewport, not
   * just below the mobile breakpoint. Use for desktop-fullscreen
   * surfaces like ContentGenerationModal (admin workspace that needs
   * the entire viewport) or ContactSelectionModal (lapsed-subscription
   * blocking modal that intentionally takes over the screen).
   *
   * Default: `false` (AppModal's own breakpoint heuristic decides).
   * When `true`, both the breakpoint and any caller `size` are
   * ignored — Mantine's `fullScreen` always wins.
   */
  forceFullScreen?: boolean;

  /** Content that fills the body (or replaces the body when `noScrollArea`). */
  children: ReactNode;
};

/**
 * Public AppModal prop shape.
 *
 * Pass-through strategy: we own `id`, `footer`, `bodyHeight`,
 * `noScrollArea`, `children`, and a small set of Mantine props that
 * AppModal applies its own logic to (`size`, `fullScreen`,
 * `classNames`). Everything else on Mantine's `ModalProps` —
 * including `styles` — is forwarded verbatim so migrations can layer
 * additive overrides (e.g. body padding). When `size` is
 * {@link AppModalSizes.form}, AppModal owns `styles.content.maxWidth`
 * ({@link APP_MODAL_FORM_MAX_WIDTH}) so call sites cannot drift.
 *
 * Why `classNames` is owned: Mantine's classNames merging is shallow,
 * so if a caller passed their own `classNames.body` it would REPLACE
 * AppModal's body class and silently drop the flex-column layout that
 * makes the footer sticky. `styles` (inline) is additive and composes
 * cleanly with our classes — caller-supplied inline styles win where
 * they conflict, which is the documented escape hatch.
 */
export type AppModalProps = AppModalOwnProps &
  Omit<MantineModalProps, keyof AppModalOwnProps | 'fullScreen' | 'classNames'>;

/**
 * Mantine xs breakpoint (576px). Matches the convention already used
 * across the codebase (e.g. FlowerOrderModal:238 reads
 * `useMediaQuery('(max-width: 576px)')`). Below this width AppModal
 * renders fullScreen so the modal width never horizontally clips on
 * iPhone SE (375px) or any narrower test viewport.
 */
const MOBILE_BREAKPOINT = '(max-width: 576px)';

/**
 * Desktop width tokens for AppModal.
 *
 * Smile create / edit / order / review dialogs MUST opt into
 * {@link AppModalSizes.form} so they share Schedule Card's shell
 * (`90vw` with a soft max of {@link APP_MODAL_FORM_MAX_WIDTH}).
 * Catalog browse previews (e-card / flower / mailed-card) use
 * {@link AppModalSizes.preview} — compact enough for media + price,
 * without the empty white of the form shell. Other dialogs (settings,
 * confirms, admin, cookies) keep Mantine `sm` / `md` / `lg` freely —
 * do not change the omit-default to form.
 *
 * When effective size is the form token, AppModal owns
 * `styles.content.maxWidth` (callers must not redefine it).
 */
export const AppModalSizes = {
  /** Smile create / edit / order / review — Schedule Card reference. */
  form: '90vw',
  /**
   * Catalog browse preview (landing / public shop tile click) and read-only
   * customer content detail (e.g. Orders order detail). Sized for media plus
   * details column; smile wizards stay on {@link form}.
   */
  preview: '700px',
  /** Short confirmations / alerts. */
  confirm: 'sm',
} as const;

/** Soft max width for {@link AppModalSizes.form} on ultrawide desktops. */
export const APP_MODAL_FORM_MAX_WIDTH = '1200px';

/**
 * True when `size` is the smile form shell (token or equivalent string).
 * Used to apply {@link APP_MODAL_FORM_MAX_WIDTH} and for Vitest guards.
 */
export function isAppModalFormSize(size: MantineModalProps['size'] | undefined): boolean {
  return size === AppModalSizes.form;
}

/**
 * True when `size` is the catalog browse preview shell.
 */
export function isAppModalPreviewSize(size: MantineModalProps['size'] | undefined): boolean {
  return size === AppModalSizes.preview;
}

/**
 * Walks `children` looking for descendant native `<button>` elements
 * (or Mantine `Button` components, which render an underlying
 * `<button>`). Used by the dev-mode runtime guardrail below to catch
 * the most common author mistake: passing `footer={null}` while still
 * rendering CTA-shaped controls inside the body.
 *
 * Heuristic only — false negatives are possible (e.g. a deeply nested
 * custom component that renders a button), but the warning fires only
 * in dev mode and only emits a console message; runtime behavior is
 * never affected.
 */
function childrenContainButton(children: ReactNode): boolean {
  let found = false;

  Children.forEach(children, (child) => {
    if (found) return;
    if (!isValidElement(child)) return;

    // Match HTML <button>.
    if (child.type === 'button') {
      found = true;
      return;
    }

    // Match Mantine Button (and any other component whose displayName
    // ends in 'Button') heuristically. Mantine 8 sets displayName to
    // '@mantine/core/Button'; matching by suffix keeps this resilient
    // to namespace prefix changes and also catches plain 'Button'
    // exports from other libraries.
    //
    // UnstyledButton is intentionally excluded: it's the building
    // block for tile/card/inline-action surfaces (e.g. SmileChoiceModal
    // renders two large UnstyledButton tiles AS the modal's body —
    // there is no separate CTA bar to extract). Real Button-class
    // CTAs (`Button`, `ActionIcon`, `CloseButton`, `CompoundButton`)
    // still get caught.
    const componentType = child.type as { displayName?: string };
    if (
      typeof componentType !== 'string' &&
      typeof componentType.displayName === 'string' &&
      /Button$/i.test(componentType.displayName) &&
      !/UnstyledButton$/i.test(componentType.displayName)
    ) {
      found = true;
      return;
    }

    // Recurse into children if present.
    const childProps = (child.props ?? {}) as { children?: ReactNode };
    if (childProps.children !== undefined) {
      if (childrenContainButton(childProps.children)) {
        found = true;
      }
    }
  });

  return found;
}

/**
 * The application's only modal primitive. See file-level docstring for
 * the full contract.
 *
 * @example Required-footer modal (default — sticky CTA at the bottom):
 * ```tsx
 * <AppModal
 *   id="add-contact"
 *   opened={opened}
 *   onClose={onClose}
 *   title="Add Contact"
 *   size={AppModalSizes.form}
 *   footer={
 *     <Group justify="flex-end">
 *       <Button variant="default" onClick={onClose}>Cancel</Button>
 *       <Button onClick={onSave}>Save</Button>
 *     </Group>
 *   }
 * >
 *   <Stack>{/ form fields /}</Stack>
 * </AppModal>
 * ```
 *
 * @example Confirmation-style modal (shrink-wrap, no separate footer):
 * ```tsx
 * <AppModal
 *   id="resend-card-confirm"
 *   opened={opened}
 *   onClose={onClose}
 *   title="Resend card?"
 *   bodyHeight="auto"
 *   footer={
 *     <Group justify="flex-end">
 *       <Button variant="default" onClick={onClose}>Cancel</Button>
 *       <Button color="red" onClick={onConfirm}>Resend</Button>
 *     </Group>
 *   }
 * >
 *   <Text>This will send a duplicate to the recipient.</Text>
 * </AppModal>
 * ```
 */
export const AppModal = ({
  id,
  footer,
  bodyHeight = 'fill',
  noScrollArea = false,
  forceFullScreen = false,
  children,
  size,
  styles,
  closeOnEscape = true,
  onClose,
  opened,
  ...mantineProps
}: AppModalProps) => {
  const escapeDialogRef = useRef<HTMLElement | null>(null);
  const bindEscapeDialogRef = useCallback((node: HTMLDivElement | null) => {
    escapeDialogRef.current = (node?.closest('[role="dialog"]') as HTMLElement | null) ?? node;
  }, []);

  const routeDocumentEscapeToOnClose = closeOnEscape === false && typeof onClose === 'function';
  useModalFormEscapeClose(
    escapeDialogRef,
    routeDocumentEscapeToOnClose ? onClose : undefined,
    Boolean(opened && routeDocumentEscapeToOnClose),
  );

  // CRITICAL: pass `getInitialValueInEffect: false` so useMediaQuery
  // reads window.matchMedia SYNCHRONOUSLY on the first render. With
  // Mantine's default (`true`), the first-render value is `undefined`
  // which the hook coerces to `false` (see `return matches || false` in
  // @mantine/hooks/use-media-query.mjs). That false-on-first-render
  // breaks the open-cycle latch below: on iPhone SE we'd latch
  // `effectiveFullScreen = false` BEFORE the matchMedia read in the
  // useEffect ever runs — the modal would render centered/partial-width
  // and its CTA would be pushed below the viewport (regression caught
  // by the MTX-schedule-card sticky-footer matrix). Synchronous read on
  // mount is safe because we don't SSR and matchMedia is always
  // available in the browser.
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {
    getInitialValueInEffect: false,
  });

  // Dev-mode runtime guardrail: catch the "I built a CTA but forgot
  // the footer slot" mistake before code review. Production builds
  // strip this entirely (process.env.NODE_ENV is constant-folded).
  if (process.env.NODE_ENV !== 'production') {
    if (footer === null && childrenContainButton(children)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[AppModal id="${id}"] footer={null} but children appear to contain a Button — ` +
          'did you mean to pass the CTA in the footer slot? ' +
          'See src/components/common/AppModal.tsx for the contract.',
      );
    }
  }

  // Below the mobile breakpoint we always render fullScreen, regardless
  // of the caller's `size` prop. This guarantees no horizontal clip on
  // iPhone SE (375px) and removes the need for every migration to
  // wire its own responsive sizing. Callers with desktop-fullscreen
  // needs (admin workspace, lapsed-subscription blocker) opt in via
  // `forceFullScreen` instead of reaching past the wrapper.
  //
  // Latch the decision for the lifetime of an open cycle: once the
  // modal is open we keep `effectiveFullScreen` stable until it
  // closes, even if the viewport width crosses the mobile breakpoint
  // (e.g. when a Mantine Combobox dropdown opens and its scroll-lock
  // briefly toggles the body scrollbar, shifting viewport width by
  // ~17px through the 576px boundary). Without this latch, isMobile
  // can flip mid-life, which switches Mantine's `fullScreen` prop,
  // remounts the modal frame, and produces the "entire screen
  // flickering between two layouts" symptom on borderline-width
  // viewports while interacting with in-modal selects. The decision
  // is re-evaluated cleanly the next time the modal opens.
  const lockedFullScreenRef = useRef<boolean | null>(null);
  const prevOpenedForAnalyticsRef = useRef(false);
  const isOpen = opened === true;

  // GA4 funnel: fire modal_open once per open transition; modal_closed on close.
  useEffect(() => {
    if (isOpen && !prevOpenedForAnalyticsRef.current) {
      beginModalAnalyticsSession(id);
      trackModalOpen(id);
    } else if (!isOpen && prevOpenedForAnalyticsRef.current) {
      const closePayload = getModalAnalyticsClosePayload(id);
      trackModalClosed({
        modalId: id,
        lastStepNumber: closePayload.lastStepNumber,
        lastStepName: closePayload.lastStepName,
        completed: closePayload.completed,
      });
    }
    prevOpenedForAnalyticsRef.current = isOpen;
  }, [isOpen, id]);

  if (!isOpen) {
    // Reset on close so the next open re-evaluates against the current
    // viewport (user may have rotated or resized between opens).
    lockedFullScreenRef.current = null;
  } else if (lockedFullScreenRef.current === null) {
    // First render where the modal is open — capture the decision and
    // hold it for the rest of this open cycle. Safe to latch
    // immediately because useMediaQuery is configured with
    // `getInitialValueInEffect: false` above, so `isMobile` is the
    // ACTUAL matchMedia value on this render (not the false-fallback
    // we'd get with the hook's default behavior).
    lockedFullScreenRef.current = forceFullScreen || isMobile === true;
  }
  const effectiveFullScreen =
    lockedFullScreenRef.current ?? (forceFullScreen || isMobile === true);

  // Coerce bodyHeight='auto' to 'fill' whenever the modal renders
  // fullScreen. The whole point of 'auto' is to shrink-wrap the modal
  // to its content; once Mantine sizes the modal to the full viewport
  // (mobile or forceFullScreen), shrink-wrap becomes meaningless and
  // only its side-effect — `overflow: visible` on the body — survives.
  // That side-effect lets long content (e.g. UpgradeModal's plan view
  // on iPhone SE) push the footer below the viewport, breaking the
  // sticky-footer contract this whole component exists to enforce.
  // 'fill' restores the scrollable-body / pinned-footer layout that
  // every fullScreen modal needs.
  const effectiveBodyHeight: AppModalBodyHeight = effectiveFullScreen ? 'fill' : bodyHeight;

  // Memo the classNames so Mantine doesn't re-allocate styles on every
  // parent render. Classes themselves are content-stable. Keying on
  // `effectiveBodyHeight` (not the raw prop) means fullScreen mobile
  // modals always get the fill-height content/body classes — see the
  // effectiveBodyHeight rationale above.
  const classNames = useMemo(
    () => ({
      content: effectiveBodyHeight === 'auto' ? classes.contentAuto : classes.content,
      body: effectiveBodyHeight === 'auto' ? classes.bodyAuto : classes.body,
    }),
    [effectiveBodyHeight],
  );

  // Pick the body-content class based on (in priority order):
  //   1. noScrollArea — caller manages overflow itself
  //   2. bodyHeight='auto' — confirmation-modal shrink-wrap (desktop only)
  //   3. default — scrollable region with internal overflow
  const scrollableClass = noScrollArea
    ? classes.scrollableBodyNoArea
    : effectiveBodyHeight === 'auto'
    ? classes.scrollableBodyAuto
    : classes.scrollableBody;

  // Desktop width: keep omit-default as Mantine `lg` so unrelated
  // dialogs that omit `size` are not silently widened. Smile create /
  // edit / order / review opt in with `size={AppModalSizes.form}`;
  // catalog browse previews use `AppModalSizes.preview`.
  const effectiveSize = effectiveFullScreen ? undefined : (size ?? 'lg');
  const useFormShell = !effectiveFullScreen && isAppModalFormSize(effectiveSize);

  // Viewport shell via the styles API (inline) so every AppModal —
  // including ones written in the future — pins its footer regardless
  // of stylesheet order. Caller `styles` merge in; shell + form maxWidth
  // win on conflict. CSS module + appModalViewportShell.css remain as
  // progressive-enhancement / defense-in-depth.
  const mergedStyles = useMemo(() => {
    const shell = buildAppModalViewportShellStyles(effectiveBodyHeight, effectiveFullScreen);

    const callerContent =
      styles && typeof styles === 'object' && 'content' in styles
        ? (styles.content as Record<string, unknown> | undefined)
        : undefined;
    const callerBody =
      styles && typeof styles === 'object' && 'body' in styles
        ? (styles.body as Record<string, unknown> | undefined)
        : undefined;

    return {
      ...styles,
      content: {
        ...callerContent,
        ...shell.content,
        ...(useFormShell
          ? {
              maxWidth: APP_MODAL_FORM_MAX_WIDTH,
              margin: '0 auto',
            }
          : null),
      },
      body: {
        ...callerBody,
        ...shell.body,
      },
    };
  }, [effectiveBodyHeight, effectiveFullScreen, useFormShell, styles]);

  return (
    <Modal
      {...mantineProps}
      opened={opened}
      onClose={onClose}
      closeOnEscape={closeOnEscape}
      size={effectiveSize}
      fullScreen={effectiveFullScreen}
      classNames={classNames}
      styles={mergedStyles}
      // The id propagates to the dialog's data-modal-id attribute so
      // Playwright tests and the runtime DOM both have a stable hook.
      // Mantine spreads unknown props onto the dialog content via the
      // standard React `data-*` attribute pass-through.
      data-modal-id={id}
      data-app-modal-shell
      // Lets appModalViewportShell.css target fill vs auto after the Mantine
      // monolith without relying on hashed CSS-module class names.
      data-app-modal-body-height={effectiveBodyHeight}
    >
      {/*
        Modal.Body is configured (via classNames.body above) as a flex
        column. The scrollable region and the footer are flex siblings
        inside it — flex-shrink:0 on the footer and flex:1 on the
        scrollable region is what actually pins the footer.
      */}
      {/*
       * `data-app-modal-scroll-body` is the stable selector callers use
       * to programmatically scroll the modal body (e.g. wizard "scroll
       * to top of next step" behavior). CSS-module class names are
       * hashed at build time, so a `data-*` attribute is the only
       * compile-stable hook. Used by FlowerOrderModal's setActive.
       */}
      <div ref={bindEscapeDialogRef} data-app-modal-escape-root style={{ display: 'contents' }}>
        <div className={scrollableClass} data-app-modal-scroll-body>{children}</div>
        {footer !== null && <div className={classes.footer}>{footer}</div>}
      </div>
    </Modal>
  );
};
